import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@environment/environment';
import { Card, Payments, Square, SuccessfulTokenResult } from '@square/web-payments-sdk-types';
import { LogService } from '../log/log';
import { TranslateService } from '@ngx-translate/core';

declare var Square: Square; // Prevents TS errors without polluting global types

@Injectable({ providedIn: 'root' })
export class SquareService {
  private readonly logService = inject(LogService);
  private readonly translateService = inject(TranslateService);
  private payments$ = signal<Payments | null>(null);

  private loadPromise: Promise<void> | null = null;
  private readonly loaded$ = signal<boolean>(false);
  readonly loaded = this.loaded$.asReadonly();

  loadSdk(): Promise<void> {
    if (this.loaded$()) return Promise.resolve();
    if (this.loadPromise) return this.loadPromise;

    this.loadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = environment.squareLink;
      script.async = true;

      script.onload = () => {
        this.loaded$.set(true);
        resolve();
      };

      script.onerror = (err) => {
        reject(`[SquareService] Square SDK failed to load: ${err}`);
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }


  /**
   * Initialize the SDK client with your Square application information.
   */
  async init() {
    await this.loadSdk();

    if (!Square) throw new Error('[SquareService] Square SDK not available after loading.');

    if (!this.payments$()) {
      this.payments$.set(Square.payments(environment.squareApplicationId, environment.squareLocationId));
    }
    this.logService.info('[SquareService] initialized');
  }

  /**
   * Create a Card payment method.
   */
  async createCard(containerId: string) {
    const payments = this.payments$();
    if (!payments) throw new Error('[SquareService] Not initialized.');
    const card = await payments.card();
    await card.attach(`#${containerId}`);
    return card;
  }

  /**
   * Create a Apple Pay payment method.
   */
  async createApplePay() {
    const payments = this.payments$();
    if (!payments) throw new Error('[SquareService] Not initialized.');
    const paymentRequest = payments.paymentRequest({
      countryCode: 'US',
      currencyCode: 'USD',
      total: {
        amount: '1.00',
        label: 'Total',
      },
    });
    const applePay = await payments.applePay(paymentRequest);
    return applePay;
  }

  /**
   * Create a Google Pay payment method.
   */
  async createGooglePay(containerId: string) {
    const payments = this.payments$();
    if (!payments) throw new Error('[SquareService] Not initialized.');
    const paymentRequest = payments.paymentRequest({
      countryCode: 'US',
      currencyCode: 'USD',
      total: {
        amount: '1.00',
        label: 'Total',
      },
    });
    const googlePay = await payments.googlePay(paymentRequest);
    await googlePay.attach(`#${containerId}`, { buttonSizeMode: 'fill', buttonType: 'short' });
    return googlePay;
  }

  /**
   * Tokenize card for backend processing.
   */
  async tokenize(card: Card): Promise<SuccessfulTokenResult> {
    const result = await card.tokenize();
    if (result.status !== 'OK') {
      throw new Error('[SquareService] Failed To Tokenize Card');
    }
    return result;
  }
}